/**
 * Inline citation pill — Perplexity-style.
 *
 * Renders the citation number as a small clickable pill. On hover, a
 * floating preview shows the entity label. On click, opens the URL in
 * a new tab.
 *
 * ONE exception — councillor citations. A person is NEVER shown as a number:
 * a `councillor`-type citation renders as a small circular AVATAR (the
 * portrait), or, when no portrait resolves (or it 404s), a subtle person
 * glyph. Both keep the same click-through and hover card. Only `speech`
 * citations reach this component with a numeric pill (affair/dispatch link by
 * their bill number via BillButton, never as a CitationPill).
 *
 * Positioning is delegated to @floating-ui/react which gives us:
 *   - automatic flip / shift to stay inside the viewport
 *   - portal rendering to escape the chat widget's `overflow: hidden`
 *   - sensible defaults for hover/focus interactions
 */
import { useState } from 'react';
import { User } from 'lucide-react';
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';
import type { Citation } from '../lib/innoq';
import { safeNavUrl } from '../lib/safeUrl';
import { useT } from '../i18n';

interface CitationPillProps {
  citation: Citation;
}

const TYPE_LABEL_KEYS: Record<NonNullable<Citation['type']>, string> = {
  councillor: 'citation.type.councillor',
  affair:     'citation.type.affair',
  dispatch:   'citation.type.dispatch',
  speech:     'citation.type.speech',
};

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function CitationPill({ citation }: CitationPillProps) {
  const [open, setOpen] = useState(false);
  // Councillor avatar 404 → fall back to the person glyph (never the number).
  const [portraitFailed, setPortraitFailed] = useState(false);
  const t = useT();

  // A councillor is a PERSON — render an avatar/glyph, never a number.
  const isCouncillor = citation.type === 'councillor';

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover   = useHover(context, { delay: { open: 0, close: 100 } });
  const focus   = useFocus(context);
  const dismiss = useDismiss(context);
  const role    = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Protocol-check the model-supplied citation URL (Audit H2). Rejecting
    // here is the safer behaviour than rendering a broken link — citation
    // URLs come from the model's `enrich_response.citations`, downstream of
    // user-controlled query text.
    const safe = safeNavUrl(citation.url);
    if (safe) window.open(safe, '_blank', 'noopener,noreferrer');
  };

  const typeLabel = citation.type ? t(TYPE_LABEL_KEYS[citation.type]) : t('citation.source');
  const host      = hostnameOf(citation.url);

  const showAvatar = isCouncillor && !!citation.portrait && !portraitFailed;

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({ onClick: handleClick })}
        type="button"
        className={
          isCouncillor
            ? `
              ml-[3px] mr-[1px] inline-flex items-center justify-center align-middle
              w-[18px] h-[18px] rounded-full overflow-hidden
              border border-[var(--accent)]/25
              ${showAvatar ? 'bg-[var(--surface)]' : 'bg-[var(--accent)]/10 text-[var(--accent)]'}
              hover:border-[var(--accent)]
              transition-colors
            `
            : `
              ml-[3px] mr-[1px] inline-flex items-center justify-center
              text-[11px] font-mono font-semibold leading-none
              min-w-[16px] h-[15px] px-[5px] rounded-full
              bg-[var(--accent)]/10 text-[var(--accent)]
              border border-[var(--accent)]/25
              hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]
              transition-colors
            `
        }
        style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
        aria-label={
          isCouncillor
            ? t('citation.aria').replace('{id}', citation.label).replace('{label}', typeLabel)
            : t('citation.aria').replace('{id}', String(citation.id)).replace('{label}', citation.label)
        }
      >
        {isCouncillor ? (
          showAvatar ? (
            <img
              src={citation.portrait as string}
              alt=""
              aria-hidden="true"
              loading="lazy"
              onError={() => setPortraitFailed(true)}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            // No portrait (or it 404'd) → subtle person glyph, never a number.
            <User size={11} strokeWidth={2.25} aria-hidden="true" />
          )
        ) : (
          citation.id
        )}
      </button>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={{
              ...floatingStyles,
              width: 240,
              maxWidth: 'calc(100vw - 16px)',
              zIndex: 10000,
            }}
            className="
              pointer-events-none
              px-2.5 py-1.5 rounded-[var(--r2)]
              bg-[var(--text-1)] text-white
              shadow-lg
              text-[11px] font-sans leading-snug
              break-words
            "
          >
            <span className="block text-[11px] font-mono uppercase tracking-wider opacity-70 mb-0.5">
              {typeLabel}
            </span>
            <span className="block font-medium">{citation.label}</span>
            {host && (
              <span className="block text-[9.5px] font-mono opacity-60 mt-0.5 truncate">
                {host}
              </span>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
